import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@/pages/common';
import { Input, Button } from 'gatorcomponents';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from '@/redux/actions/notifications';
import { notifierSuccess } from '@/utils/Application/notifier';
import { loadSummary } from '@/redux/actions/summary';
import * as Styles from './ValidateCodeModal.styles';
import {
  resendTwoFactorAuthCode,
  setValidationFeedback,
  validateTwoFactorAuthCode,
  generateCode,
} from '@/redux/actions/twoFactorAuth';
import { ResendCode } from './ResendCode';
import { useCountdown } from '@/hooks/useCountdown';
import { commonActions } from '@/redux/modules';

const CODE_LENGTH = 6;

const ValidateCodeModal = ({
  modal,
  toggleValidationCodeFlow,
  goToChooseMethodStep,
  userEmail,
  userPhone,
  twoFactorIsEnabled,
  twoFactorAuthLocale,
}) => {
  const [resendEnabled, setResendEnabled] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
  });
  const testId = `validate-code-modal--${modal.type}`;

  const dispatch = useDispatch();
  const {
    loadingResend,
    loadingValidate,
    validationError,
    loadingCodeGeneration,
    selectedMethodData,
  } = useSelector(state => state.twoFactorAuth);

  const onFinishTimeout = () => {
    setResendEnabled(true);
  };

  const {
    startTimer,
    time,
  } = useCountdown({ onFinish: onFinishTimeout });

  const showCodeRequestSuccessSnackbar = useCallback((attemptsLeft) => {
    const toast = {
      title: twoFactorAuthLocale.snackbarRequestLimit.title,
      info: twoFactorAuthLocale.snackbarRequestLimit.label(attemptsLeft),
      showIcon: true,
    };

    dispatch(commonActions.notifications.set({ toast, type: 'success' }));
  }, [
    dispatch,
    twoFactorAuthLocale,
  ]);

  const showValidateSnackbar = () => {
    toggleValidationCodeFlow();
    dispatch(setValidationFeedback({ hasError: false }));

    const snackbarType = twoFactorIsEnabled ? {} : notifierSuccess;
    dispatch(enqueueSnackbar(
      twoFactorAuthLocale.snackbarFeedback(modal.type),
      snackbarType,
    ));
  };

  const onSubmit = () => {
    dispatch(validateTwoFactorAuthCode({
      code: formData.code,
      action: twoFactorIsEnabled ? 'disable' : 'enable',
    })).then(({ payload }) => {
      const codeValidated = payload.success;

      codeValidated && dispatch(loadSummary()).then(showValidateSnackbar);
      !codeValidated && dispatch(setValidationFeedback({ hasError: true }));

      codeValidated && setResendEnabled(true);
      setFormData(prev => ({ ...prev, code: '' }));
    });
  };

  const handleChange = useCallback(({ key, value }) => {
    dispatch(setValidationFeedback({ hasError: false }));
    setFormData({
      ...formData,
      [key]: value,
    });
  }, [
    formData,
    dispatch,
  ]);

  const resendCode = () => {
    dispatch(resendTwoFactorAuthCode({ method: modal.method })).then(({ payload }) => {
      startTimer(({ deadline: payload.nextCodeCanBeGeneratedAt }));
      payload.isSent && showCodeRequestSuccessSnackbar(payload.method.left);
    });
    setResendEnabled(false);
  };

  const onOpenModal = useCallback(() => {
    dispatch(setValidationFeedback({ hasError: false }));

    dispatch(generateCode({
      method: modal.method,
      action: twoFactorIsEnabled ? 'disable' : 'enable',
    })).then(({ payload }) => {
      startTimer(({ deadline: payload.nextCodeCanBeGeneratedAt }));
      payload.isSent && showCodeRequestSuccessSnackbar(payload.method.left);
    });
  }, [modal,
    twoFactorIsEnabled,
    dispatch,
    showCodeRequestSuccessSnackbar,
  ]);

  useEffect(() => {
    onOpenModal();
  }, [onOpenModal]);

  const descriptions = {
    email: { message: twoFactorAuthLocale.validateCodeModal.descriptionEmail, value: userEmail },
    sms: { message: twoFactorAuthLocale.validateCodeModal.descriptionSms, value: userPhone },
    whatsapp: { message: twoFactorAuthLocale.validateCodeModal.descriptionWhatsApp, value: userPhone },
  };

  const description = descriptions[modal.method];

  return (
    <Modal
      maxWidth={730}
      onClose={toggleValidationCodeFlow}
      closeOnOutsideClick={false}
      testId={testId}
    >
      <Styles.Container>
        <Styles.Title>{modal.title}</Styles.Title>

        <Styles.Description data-testid={`${testId}__description-${modal.method}`}>
          {description.message}
          <div>
            <b>{description.value}</b>
          </div>
        </Styles.Description>

        <Styles.Form>
          <Input
            name="code"
            size="lg"
            maxLength={CODE_LENGTH}
            errorMessage={validationError ? twoFactorAuthLocale.validateCodeModal.codeInputError : undefined}
            value={formData.code}
            placeholder={twoFactorAuthLocale.validateCodeModal.codeInputPlaceholder}
            onChange={e => handleChange({ key: 'code', value: e.target.value })}
            testId={`${testId}__input-code`}
          />

          <Button
            label={modal.primaryButtonLabel}
            onClick={onSubmit}
            disabled={loadingValidate}
            loading={loadingValidate}
            size="large"
            testId={`${testId}__button`}
          />
        </Styles.Form>

        {loadingCodeGeneration || selectedMethodData.left > 0 ? (
          <ResendCode
            resendEnabled={resendEnabled}
            loadingResend={loadingResend || loadingCodeGeneration}
            resendCode={resendCode}
            time={time}
            twoFactorAuthLocale={twoFactorAuthLocale}
            testId={testId}
          />
        ) : (
          <Styles.RequestsLimitWrapper>
            <Styles.RequestLimitTitle>
              {twoFactorAuthLocale.chooseMethodModal.requestLimit.title}
            </Styles.RequestLimitTitle>

            {twoFactorAuthLocale.chooseMethodModal.requestLimit.description}

            <Styles.BackButtonWrapper>
              <Button
                label={twoFactorAuthLocale.chooseMethodModal.requestLimit.buttonLabel}
                variant="secondary"
                size="medium"
                onClick={goToChooseMethodStep}
              />
            </Styles.BackButtonWrapper>
          </Styles.RequestsLimitWrapper>
        )}

      </Styles.Container>
    </Modal>
  );
};

export default ValidateCodeModal;
